import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Skeleton, Space, Spin, Typography } from 'antd';
import { PmsAPI } from '../../../../controller/PmsAPI';
import InventoryFilter, { defaultRange, MAX_DAYS } from './components/InventoryFilter';
import InventoryMatrix from './components/InventoryMatrix';
import {
  buildDateColumns,
  buildRoomTypeRows,
  buildTotalCells,
  DATE_FORMAT,
} from './inventoryData';

const { Text, Title } = Typography;

export default function InventoryPage() {
  const [range, setRange] = useState(defaultRange);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  // 上一次「實際送出查詢」的區間 —— payload.days 是依這個區間查回來的，
  // 表格必須永遠以它為準，不可用使用者正在編輯、尚未送出查詢的 range 計算，
  // 否則調整日期但還沒按查詢時，會把新區間套在舊資料上，
  // 誤判成「PMS 尚未開放」(其實只是還沒查而已，不是真的沒房)
  const [queriedRange, setQueriedRange] = useState(null);

  // targetRange 可明確指定 —— 選完日期就查時，setRange 尚未反映到 state，
  // 沿用閉包裡的舊 range 會查到上一次的區間
  const query = useCallback(async (targetRange = range) => {
    const [from, to] = targetRange;
    setLoading(true);
    setError(null);
    try {
      const response = await PmsAPI.inventory.index(
        from.format(DATE_FORMAT),
        to.format(DATE_FORMAT)
      );
      setPayload(response.data);
      setQueriedRange([from.clone(), to.clone()]);
    } catch (e) {
      // 失敗時清掉舊資料 —— 留著過期表格比顯示錯誤更危險
      setPayload(null);
      setQueriedRange(null);
      setError(e?.response?.data?.message || '查詢失敗，請稍後重試');
    } finally {
      setLoading(false);
    }
  }, [range]);

  // 選完日期立即查詢，不必再按「查詢」。
  // 超出天數上限時不送出 —— 後端會回 422，讓 InventoryFilter 先擋下並顯示提示。
  const handleRangeChange = useCallback((next) => {
    if (!next || !next[0] || !next[1]) return;
    setRange(next);
    if (next[1].diff(next[0], 'days') + 1 > MAX_DAYS) return;
    query(next);
  }, [query]);

  useEffect(() => {
    query();
    // 僅在初次載入時查詢，之後由選擇日期區間 (或按「查詢」重新整理) 觸發
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 表格資料一律用「上次送出查詢」的區間 (queriedRange)，不是即時的 range state
  const dateColumns = payload && queriedRange
    ? buildDateColumns(queriedRange[0].format(DATE_FORMAT), queriedRange[1].format(DATE_FORMAT), payload.days)
    : [];
  const rows = buildRoomTypeRows(dateColumns, payload?.days, payload?.room_types);
  const totalCells = buildTotalCells(dateColumns, payload?.days);

  // 日期區間已變更、但還沒按查詢 —— 目前畫面是舊區間的結果，
  // 必須明確提示，不能讓人誤以為是目前選取區間的結果
  const filterDirty = Boolean(
    payload
    && queriedRange
    && (
      !range[0].isSame(queriedRange[0], 'day')
      || !range[1].isSame(queriedRange[1], 'day')
    )
  );

  return (
    <Card>
      <Title level={4}>房型庫存查詢</Title>

      <InventoryFilter
        range={range}
        loading={loading}
        onRangeChange={handleRangeChange}
        onQuery={() => query()}
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message="無法取得 PMS 庫存"
          description={error}
          action={<Button size="small" onClick={() => query()}>重試</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 選完日期會立即查詢，所以這個提示多半只在查詢往返的瞬間出現；
          真正會停留的情況是區間超過天數上限而沒有送出查詢。 */}
      {!error && filterDirty && (
        <Alert
          type="warning"
          showIcon
          message="日期區間已變更，以下仍是上次查詢的結果"
          style={{ marginBottom: 16 }}
        />
      )}

      {payload?.updated_at && (
        <Space style={{ marginBottom: 8 }}>
          <Text type="secondary">資料更新時間：{payload.updated_at}</Text>
        </Space>
      )}

      {/* 「線上銷售」是剩餘而非原始配額：OTA 賣出時德安直接減少 BLOCK_QNT，LEFT_QNT 隨之同降，
          故快照上兩者永遠相等 (BLOCKINV_MN.ORDER_QNT 恆為 0 只代表該欄未被使用)。
          兩欄可以相加 —— 它們是同一批未售房，差別只在由哪個通路賣。 */}
      {payload && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            <Text strong>現場可售</Text>是櫃台/直訂可以立刻賣的房數；
            <Text strong>線上銷售</Text>是仍保留給網路通路 (Booking、易遊網、Expedia、官網等)、
            尚未被訂走的房數，OTA 賣得到，現場要賣需請訂房組釋出。
            <Text strong>兩者相加就是當日可賣的總量</Text>，也就是
            <Text strong> 未售小計</Text> (再加上其他保留)，等於總房數扣掉已訂、在住與維修。
            此欄對應德安<Text strong>房況</Text>畫面的空房；德安<Text strong>庫存</Text>畫面的空房則是本表的現場可售。
            <br />
            <Text strong style={{ color: '#d46b08' }}>橘底的格子</Text>
            代表該房型當天還有 2 間以上未售，但其中一邊通路已無庫存可賣，
            建議請訂房組調整配置以免少賣 —— 滑鼠移上去有具體建議。
          </Text>
        </div>
      )}

      {/* payload 為 null 時 (首次載入、或按「重試」後尚未回來) 絕不能渲染表格 ——
          dataSource=[] 會讓 antd Table 顯示「暫無資料」，業務可能把它讀成「沒房」。
          正常情況下這個狀態不到一秒就過去，但 VPN 靜默丟包、Oracle 連線沒設 timeout 時
          可能停留數十秒，此時要讓「還在查」跟「查到的資料是空的」在畫面上明顯不同。 */}
      {!error && (
        payload ? (
          <Spin spinning={loading} tip="查詢庫存中">
            <InventoryMatrix
              dateColumns={dateColumns}
              rows={rows}
              totalCells={totalCells}
            />
          </Spin>
        ) : (
          <Skeleton active paragraph={{ rows: 6 }} />
        )
      )}
    </Card>
  );
}
